/**
 * Storage upload size limits, enforced in the browser BEFORE any bytes leave.
 *
 * Supabase Storage refuses an object over its bucket's `file_size_limit` (or
 * the project-wide limit when the bucket has none) with HTTP 413 — but only
 * after the browser has sent the whole file. On a phone that is a long wait
 * for a vague "upload failed", and an anonymous lead-form visitor retried a
 * too-large photo three times in 30 seconds (2026-10-07).
 *
 * `guardStorageUploads` (installed on the one app client in
 * integrations/supabase/client.ts) checks every `upload` / `update` /
 * `uploadToSignedUrl` against these numbers and answers with a clear
 * `UploadTooLargeError` instead of sending the request. Flows that can shrink
 * a photo first (shrinkImageToFit) do that before they upload.
 *
 * SOURCE OF TRUTH is the database (`storage.buckets.file_size_limit`, set by
 * migrations) and the hosted project storage setting (150 MiB, matching
 * supabase/config.toml). The pgTAP test
 * supabase/tests/246-storage-bucket-size-limits_test.sql pins the same
 * numbers: change a bucket limit in a migration and that test fails until
 * this table is updated too.
 */

const MiB = 1024 * 1024;

/** Project-wide storage limit (hosted setting + config.toml `[storage] file_size_limit`). */
export const PROJECT_MAX_UPLOAD_BYTES = 150 * MiB;

/** Per-bucket `file_size_limit` in bytes. Buckets without one use the project limit. */
export const BUCKET_MAX_UPLOAD_BYTES: Readonly<Record<string, number>> = {
  'auto-proposals': 10485760,
  'business-documents': 26214400,
  'catalog-photos': 10485760,
  'commercial-packet-assets': 20971520,
  'crew-documents': 10485760,
  'customer-attachments': 157286400,
  'customer-covers': 10485760,
  'demo-library-posters': 10485760,
  'demo-library-sources': 157286400,
  'inventory-photos': 10485760,
  'job-photos': 52428800,
  'job-signoffs': 10485760,
  'lead-collection-uploads': 10485760,
  'lead-form-gallery': 10485760,
  measurements: 52428800,
  'payment-photos': 10485760,
  'preset-showcase-media': 157286400,
  'profile-pictures': 5242880,
  'proposal-contracts': 27262976,
  'quote-pdfs': 20971520,
  'quote-photos': 10485760,
  'sms-outbound': 4800000,
  'support-attachments': 52428800,
  'trial-voice-recordings': 52428800,
};

/** The most bytes one object in `bucket` may hold. */
export function maxUploadBytes(bucket: string): number {
  return Math.min(BUCKET_MAX_UPLOAD_BYTES[bucket] ?? PROJECT_MAX_UPLOAD_BYTES, PROJECT_MAX_UPLOAD_BYTES);
}

/** "10 MB", "4.6 MB", "150 MB" — binary megabytes, the unit the limits are set in. */
export function formatUploadMb(bytes: number): string {
  const mb = bytes / MiB;
  const rounded = mb >= 10 ? Math.round(mb) : Math.round(mb * 10) / 10;
  return `${rounded} MB`;
}

export function uploadTooLargeMessage(name: string | null | undefined, size: number, maxBytes: number): string {
  const what = name ? `${name} is` : 'This file is';
  return `${what} ${formatUploadMb(size)}. The limit is ${formatUploadMb(maxBytes)} — choose a smaller file.`;
}

/**
 * The size of an upload body as Storage will receive it, or null when it
 * cannot be known without reading it (streams). FormData bodies sum their
 * Blob/File parts.
 */
export function uploadBodySize(body: unknown): number | null {
  if (body == null) return null;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return body.size;
  if (body instanceof ArrayBuffer) return body.byteLength;
  if (ArrayBuffer.isView(body)) return body.byteLength;
  if (typeof body === 'string') return new TextEncoder().encode(body).byteLength;
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    let total = 0;
    body.forEach((value) => {
      if (typeof Blob !== 'undefined' && value instanceof Blob) total += value.size;
    });
    return total;
  }
  return null;
}

function uploadBodyName(body: unknown, path: string): string {
  if (typeof File !== 'undefined' && body instanceof File && body.name) return body.name;
  const last = path.split('/').pop();
  return last || 'This file';
}

/**
 * Shaped like storage-js's StorageApiError (`isStorageError()` recognises it,
 * `status` 413), so every caller's existing `if (error)` path handles it and a
 * caller that shows `error.message` shows the clear limit text.
 */
export class UploadTooLargeError extends Error {
  readonly __isStorageError = true;
  readonly status = 413;
  readonly statusCode = '413';
  readonly code = 'upload_too_large';
  constructor(
    message: string,
    readonly bucket: string,
    readonly size: number,
    readonly maxBytes: number,
  ) {
    super(message);
    this.name = 'UploadTooLargeError';
  }
}

export function isUploadTooLargeError(error: unknown): error is UploadTooLargeError {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { code?: unknown }).code === 'upload_too_large'
  );
}

/** Null when `body` fits `bucket`; otherwise the error to return instead of uploading. */
export function checkUploadSize(bucket: string, path: string, body: unknown): UploadTooLargeError | null {
  const size = uploadBodySize(body);
  const max = maxUploadBytes(bucket);
  if (size === null || size <= max) return null;
  return new UploadTooLargeError(uploadTooLargeMessage(uploadBodyName(body, path), size, max), bucket, size, max);
}

type UploadResult = { data: null; error: UploadTooLargeError };
type FileApiLike = Record<string, unknown>;
type StorageLike = { from: (id: string) => unknown };

/** Methods whose (path, body) — or (path, token, body) — carry new bytes. */
const BODY_ARG_INDEX: Record<string, number> = { upload: 1, update: 1, uploadToSignedUrl: 2 };

/**
 * Wrap `storage.from(bucket)` so oversized uploads are refused locally.
 * Honours storage-js's `throwOnError()` mode by throwing instead of
 * returning. Idempotent.
 */
export function guardStorageUploads<T extends { storage: StorageLike }>(client: T): T {
  const storage = client.storage as StorageLike & { __uploadGuard?: true };
  if (storage.__uploadGuard) return client;
  const originalFrom = storage.from.bind(storage);
  storage.from = (bucket: string) => {
    const api = originalFrom(bucket) as FileApiLike;
    for (const [method, bodyIndex] of Object.entries(BODY_ARG_INDEX)) {
      const original = api[method];
      if (typeof original !== 'function') continue;
      api[method] = (...args: unknown[]) => {
        const tooLarge = checkUploadSize(bucket, String(args[0] ?? ''), args[bodyIndex]);
        if (tooLarge) {
          if ((api as { shouldThrowOnError?: boolean }).shouldThrowOnError) return Promise.reject(tooLarge);
          return Promise.resolve<UploadResult>({ data: null, error: tooLarge });
        }
        return (original as (...a: unknown[]) => unknown).apply(api, args);
      };
    }
    return api;
  };
  storage.__uploadGuard = true;
  return client;
}
